import React, { useState, useEffect } from "react";
import { supabase } from "../../supabaseClient";
import { useNavigate } from "react-router-dom";
import { 
  Briefcase, 
  ChevronDown, 
  Calendar, 
  MapPin, 
  Users, 
  Trash2, 
  Edit, 
  Plus,
  Clock
} from "lucide-react";

export default function AdminTryoutDashboard() {
  const navigate = useNavigate();

  // --- STATE ---
  const [selectedProgramId, setSelectedProgramId] = useState(null);
  const [adminPrograms, setAdminPrograms] = useState([]);
  
  const [tryouts, setTryouts] = useState([]);
  const [registrations, setRegistrations] = useState({});
  const [loading, setLoading] = useState(true);
  
  // UI Controls
  const [tab, setTab] = useState("tryouts");
  const [filter, setFilter] = useState("upcoming");

  // --- 1. FETCH ADMIN PROGRAMS (Initial Load) ---
  useEffect(() => {
    async function fetchAdminPrograms() {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const { data, error } = await supabase
          .from('programs')
          .select('id, name')
          .eq('administrator', user.id);

        if (!error && data && data.length > 0) {
          setAdminPrograms(data);
          setSelectedProgramId(data[0].id);
        } else {
            setLoading(false);
        }
      } catch (err) {
        console.error("Error fetching programs:", err);
        setLoading(false);
      }
    }
    fetchAdminPrograms();
  }, []);

  // --- 2. FETCH TRYOUTS & REGISTRATIONS (Adjusted for Schema) ---
  useEffect(() => {
    if (!selectedProgramId) return;

    async function fetchData() {
      setLoading(true);
      try {
        // A. Fetch Tryouts directly using program_id
        // We join 'teams' just to get the name for display
        const { data: tryoutData, error: tryoutError } = await supabase
          .from("tryouts")
          .select(`
            *,
            teams ( id, name )
          `)
          .eq("program_id", selectedProgramId)
          .order("start_time", { ascending: true });

        if (tryoutError) throw tryoutError;

        setTryouts(tryoutData || []);

        // B. Fetch Registrations if tryouts exist
        const tryoutIds = tryoutData.map(t => t.id);
        
        if (tryoutIds.length > 0) {
            /* CRITICAL UPDATE:
               We use 'users!athlete_id' because your table uses 'athlete_id' 
               as the foreign key to the users table.
            */
            const { data: regData, error: regError } = await supabase
                .from("tryout_registrations")
                .select(`
                    id, tryout_id, registered_at, status,
                    athlete:users!athlete_id ( full_name, email, profile_img )
                `)
                .in("tryout_id", tryoutIds);

            if (regError) {
                console.error("Registration fetch error:", regError);
            } else if (regData) {
                // Group by tryout_id
                const grouped = {};
                regData.forEach((r) => {
                    if (!grouped[r.tryout_id]) grouped[r.tryout_id] = [];
                    grouped[r.tryout_id].push(r);
                });
                setRegistrations(grouped);
            }
        } else {
            setRegistrations({});
        }

      } catch (err) {
        console.error("Error loading dashboard:", err);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [selectedProgramId]);

  // --- ACTIONS ---
  const deleteTryout = async (id) => {
    if (!window.confirm("Delete this tryout?")) return;
    try {
        await supabase.from("tryouts").delete().eq("id", id);
        setTryouts(prev => prev.filter(t => t.id !== id));
    } catch (err) {
        console.error("Delete failed", err);
    }
  };

  const handleCreate = () => {
      // Pass the selected program ID to the create page
      navigate(`/admin/program/${selectedProgramId}/tryouts/create`); 
  };

  // --- FILTERS ---
  const filteredTryouts = tryouts.filter((t) => {
    if (filter === "all") return true;
    // Use start_time for comparison
    return new Date(t.start_time) >= new Date();
  });

  return (
    <div className="min-h-screen bg-gray-100 pb-20 font-sans">
      
      {/* HEADER */}
      <div className="bg-white px-6 py-4 shadow-sm border-b border-gray-100 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 text-blue-600 rounded-lg">
                <Calendar size={20} />
            </div>
            <h1 className="text-xl font-extrabold text-gray-900 tracking-tight">
                Tryout Dashboard
            </h1>
        </div>

        {/* PROGRAM SELECTOR */}
        {adminPrograms.length > 0 && (
            <div className="relative min-w-[250px]">
                <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={16} />
                <select 
                    value={selectedProgramId || ""}
                    onChange={(e) => setSelectedProgramId(e.target.value)}
                    className="w-full pl-10 pr-10 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 font-bold text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 appearance-none cursor-pointer transition-all hover:bg-white hover:shadow-sm"
                >
                    {adminPrograms.map(prog => (
                        <option key={prog.id} value={prog.id}>
                            {prog.name}
                        </option>
                    ))}
                </select>
            </div>
        )}
      </div>

      <div className="max-w-4xl mx-auto p-4 space-y-6">

        {/* CONTROLS */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div className="bg-white p-1 rounded-xl shadow-sm border border-gray-100 inline-flex">
                <button
                    onClick={() => setFilter("upcoming")}
                    className={`px-4 py-1.5 rounded-lg text-sm font-bold transition-all ${
                        filter === "upcoming" ? "bg-blue-50 text-blue-600" : "text-gray-500 hover:text-gray-700"
                    }`}
                >
                    Upcoming
                </button>
                <button
                    onClick={() => setFilter("all")}
                    className={`px-4 py-1.5 rounded-lg text-sm font-bold transition-all ${
                        filter === "all" ? "bg-blue-50 text-blue-600" : "text-gray-500 hover:text-gray-700"
                    }`}
                >
                    History
                </button>
            </div>

            <button
                onClick={handleCreate}
                disabled={!selectedProgramId}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-xl font-bold text-sm transition-all shadow-sm hover:shadow-md disabled:opacity-50"
            >
                <Plus size={18} strokeWidth={3} />
                Create Tryout
            </button>
        </div>

        {/* TABS */}
        <div className="border-b border-gray-200 flex gap-6">
            <button
                className={`pb-3 text-sm font-bold transition-all ${
                    tab === "tryouts" 
                    ? "text-blue-600 border-b-2 border-blue-600" 
                    : "text-gray-500 hover:text-gray-800"
                }`}
                onClick={() => setTab("tryouts")}
            >
                Tryouts List
            </button>
            <button
                className={`pb-3 text-sm font-bold transition-all ${
                    tab === "athletes" 
                    ? "text-blue-600 border-b-2 border-blue-600" 
                    : "text-gray-500 hover:text-gray-800"
                }`}
                onClick={() => setTab("athletes")}
            >
                Registered Athletes
            </button>
        </div>

        {/* CONTENT */}
        {loading ? (
             <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
             </div>
        ) : filteredTryouts.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-2xl border border-dashed border-gray-300">
                <Calendar size={48} className="mx-auto mb-3 text-gray-300" />
                <h3 className="text-lg font-bold text-gray-900">No tryouts found</h3>
                <p className="text-gray-500">
                    {filter === "upcoming" ? "No upcoming tryouts scheduled." : "No tryout history found."}
                </p>
            </div>
        ) : (
            <>
                {/* CARD VIEW */}
                {tab === "tryouts" && (
                    <div className="space-y-4">
                        {filteredTryouts.map((t) => (
                            <div key={t.id} className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all">
                                <div className="flex flex-col md:flex-row justify-between gap-4">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-2">
                                            {t.teams?.name && (
                                                <span className="text-xs font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-md uppercase tracking-wide">
                                                    {t.teams.name}
                                                </span>
                                            )}
                                            {t.gender && (
                                                 <span className="text-xs font-bold text-gray-500 bg-gray-100 px-2 py-0.5 rounded-md">
                                                    {t.gender}
                                                 </span>
                                            )}
                                        </div>
                                        
                                        <h2 className="text-lg font-bold text-gray-900">{t.description || "Tryout Session"}</h2>
                                        
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-y-1 gap-x-4 mt-2">
                                            <p className="text-gray-600 font-medium text-sm flex items-center gap-1.5">
                                                <Calendar size={15} className="text-blue-500" />
                                                {new Date(t.start_time).toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' })}
                                            </p>
                                            <p className="text-gray-600 font-medium text-sm flex items-center gap-1.5">
                                                <Clock size={15} className="text-blue-500" />
                                                {new Date(t.start_time).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}
                                            </p>
                                        </div>
                                        
                                        <div className="text-gray-500 text-sm mt-1 flex items-start gap-1.5">
                                            <MapPin size={15} className="text-gray-400 mt-0.5" />
                                            <div>
                                                <span className="font-medium block">{t.location}</span>
                                                {t.location_address && <span className="text-gray-400 text-xs">{t.location_address}</span>}
                                            </div>
                                        </div>
                                        
                                        <div className="mt-4 flex items-center gap-2">
                                            <Users size={16} className="text-gray-400" />
                                            <span className="text-sm font-bold text-gray-700">
                                                {registrations[t.id]?.length || 0} Registered
                                            </span>
                                        </div>
                                    </div>

                                    <div className="flex md:flex-col gap-2 shrink-0 justify-center">
                                        <button
                                            onClick={() => navigate(`/admin/teams/${t.team_id}/tryouts/${t.id}/edit`)}
                                            className="px-4 py-2 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-xl text-sm font-bold text-gray-700 flex items-center justify-center gap-2 transition-colors"
                                        >
                                            <Edit size={16} />
                                            <span>Edit</span>
                                        </button>
                                        <button
                                            onClick={() => deleteTryout(t.id)}
                                            className="px-4 py-2 bg-red-50 hover:bg-red-100 border border-red-100 rounded-xl text-sm font-bold text-red-600 flex items-center justify-center gap-2 transition-colors"
                                        >
                                            <Trash2 size={16} />
                                            <span>Delete</span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* ROSTER VIEW */}
                {tab === "athletes" && (
                    <div className="space-y-6">
                        {filteredTryouts.map((t) => (
                            <div key={t.id} className="border border-gray-100 rounded-2xl bg-white shadow-sm overflow-hidden">
                                <div className="bg-gray-50/50 px-5 py-3 border-b border-gray-100 flex justify-between items-center">
                                    <div>
                                        <h3 className="font-bold text-gray-900 text-sm">{t.description || "Tryout Session"}</h3>
                                        <p className="text-xs text-gray-500 font-medium">
                                            {new Date(t.start_time).toLocaleDateString()} • {t.teams?.name}
                                        </p>
                                    </div>
                                    <span className="bg-white border px-3 py-1 rounded-lg text-xs font-bold shadow-sm">
                                        {registrations[t.id]?.length || 0} Athletes
                                    </span>
                                </div>

                                {(!registrations[t.id] || registrations[t.id].length === 0) ? (
                                    <div className="p-6 text-center text-gray-400 text-sm font-medium">
                                        No athletes have registered yet.
                                    </div>
                                ) : (
                                    <div className="divide-y divide-gray-100">
                                        {registrations[t.id].map((reg) => (
                                            <div key={reg.id} className="p-4 hover:bg-blue-50/30 transition-colors flex items-center justify-between">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center font-bold text-gray-500 overflow-hidden">
                                                        {reg.athlete?.profile_img ? (
                                                            <img src={reg.athlete.profile_img} alt="" className="w-full h-full object-cover"/>
                                                        ) : (
                                                            (reg.athlete?.full_name || "U").charAt(0)
                                                        )}
                                                    </div>
                                                    <div>
                                                        <p className="font-bold text-gray-800 text-sm">
                                                            {reg.athlete?.full_name || "Unknown Name"}
                                                        </p>
                                                        <p className="text-xs text-gray-500">
                                                            {reg.athlete?.email || "No Email"}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="text-right">
                                                    <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 block mb-1">Registered</span>
                                                    <span className="text-xs font-medium text-gray-600">
                                                        {new Date(reg.registered_at || Date.now()).toLocaleDateString()}
                                                    </span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </>
        )}
      </div>
    </div>
  );
}
